import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { FiEdit2, FiMail, FiPhone, FiPlus, FiX } from 'react-icons/fi';
import api from '../../../../system/client/src/services/api';
import Modal from '../../../Components/Modal';
import './ContactsPanel.css';

const EMPTY = { name: '', jobTitle: '', phone: '', email: '' };

/* The customer's own contacts at this trader (Plane FREIGHTSOU-2): name,
 * job title, phone, email. Shared with everyone in their organisation, never
 * with other FreightSource customers. Several per trader. Adding the first
 * one to an unsaved trader saves it as a Prospect (ensureSaved), like the
 * activity panel. */
const ContactsPanel = ({ traderId, ensureSaved }) => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(null); // { id?: string, name, jobTitle, phone, email }
    const [saving, setSaving] = useState(false);
    const [confirming, setConfirming] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`traders/${traderId}/contacts`)
            .then((rows) => { if (!cancelled) setContacts(rows); })
            .catch((e) => console.error('Error loading contacts:', e))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [traderId]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async (e) => {
        e.preventDefault();
        if (!form.name.trim() && !form.phone.trim() && !form.email.trim()) {
            message.error('Add a name, phone number or email.');
            return;
        }
        setSaving(true);
        try {
            const payload = { name: form.name, jobTitle: form.jobTitle, phone: form.phone, email: form.email };
            if (form.id) {
                const updated = await api.patch(`trader-contacts/${form.id}`, payload);
                setContacts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
            } else {
                if (!(await ensureSaved())) return;
                const created = await api.post(`traders/${traderId}/contacts`, payload);
                setContacts((prev) => [...prev, created]);
            }
            setForm(null);
        } catch (err) {
            message.error(err.response?.data?.error || 'Could not save that contact');
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        const c = confirming;
        if (!c) return;
        setDeleting(true);
        try {
            await api.delete(`trader-contacts/${c.id}`);
            setContacts((prev) => prev.filter((x) => x.id !== c.id));
            setConfirming(null);
        } catch {
            message.error('Could not delete that contact');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="contacts-panel">
            <span className="kicker">Your contacts</span>

            {loading ? (
                <p className="contacts-empty">Loading…</p>
            ) : (
                <>
                    {contacts.length === 0 && !form && (
                        <p className="contacts-empty">
                            Keep the people you deal with here. Only your team can see them.
                        </p>
                    )}
                    {contacts.map((c) => (form && form.id === c.id ? null : (
                        <div key={c.id} className="contact-card">
                            <div className="contact-main">
                                {c.name && <strong>{c.name}</strong>}
                                {c.job_title && <span className="contact-role">{c.job_title}</span>}
                                {c.phone && (
                                    <a href={`tel:${c.phone.replace(/[^\d+]/g, '')}`} className="contact-line">
                                        <FiPhone aria-hidden="true" /> <span>{c.phone}</span>
                                    </a>
                                )}
                                {c.email && (
                                    <a href={`mailto:${c.email}`} className="contact-line" title={c.email}>
                                        <FiMail aria-hidden="true" /> <span>{c.email}</span>
                                    </a>
                                )}
                            </div>
                            <div className="contact-actions">
                                <button
                                    type="button"
                                    aria-label="Edit contact"
                                    onClick={() => setForm({ id: c.id, name: c.name || '', jobTitle: c.job_title || '', phone: c.phone || '', email: c.email || '' })}
                                >
                                    <FiEdit2 />
                                </button>
                                <button type="button" className="danger" aria-label="Delete contact" onClick={() => setConfirming(c)}>
                                    <FiX />
                                </button>
                            </div>
                        </div>
                    )))}

                    {form ? (
                        <form className="contact-form" onSubmit={save}>
                            <input value={form.name} onChange={set('name')} placeholder="Name" maxLength={120} autoFocus />
                            <input value={form.jobTitle} onChange={set('jobTitle')} placeholder="Job title (optional)" maxLength={120} />
                            <input type="tel" value={form.phone} onChange={set('phone')} placeholder="Phone" maxLength={40} />
                            <input type="email" value={form.email} onChange={set('email')} placeholder="Email" maxLength={255} />
                            <div className="contact-form-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setForm(null)} disabled={saving}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={saving}>
                                    {saving ? 'Saving…' : form.id ? 'Save' : 'Add contact'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <button type="button" className="contacts-add" onClick={() => setForm({ ...EMPTY })}>
                            <FiPlus aria-hidden="true" /> Add contact
                        </button>
                    )}
                </>
            )}

            <Modal
                open={Boolean(confirming)}
                onClose={() => !deleting && setConfirming(null)}
                kicker="Delete"
                title="Delete this contact?"
                width={420}
                footer={
                    <>
                        <button type="button" className="btn btn-secondary" onClick={() => setConfirming(null)} disabled={deleting}>
                            Cancel
                        </button>
                        <button type="button" className="btn btn-primary" onClick={remove} disabled={deleting}>
                            {deleting ? 'Deleting…' : 'Delete'}
                        </button>
                    </>
                }
            >
                <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-2)' }}>
                    {confirming?.name || confirming?.email || confirming?.phone} will be removed for everyone in your team.
                    This can't be undone.
                </p>
            </Modal>
        </div>
    );
};

export default ContactsPanel;
